import React, { useCallback, useMemo, useState } from 'react';
import { AppButton, Box, Button, Inscription, NoUsersIcon, Preloader } from '@common-fe/common-fe';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import { DropButton } from '@/components/controls/DropButton';
import { PendingModal } from '@/components/elements';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import { ButtonHintForTerminatedEmployer } from '@/modules/employer/components/EmployerTermination/ButtonHintForTerminatedEmployer';

import AddBeneficiaryModal from './AddBeneficiaryModal';
import BeneficiariesListItem from './BeneficiariesListItem';
import useDeleteBeneficiariesQuery from './useDeleteBeneficiaries.query';
import { Beneficiary, BeneficiaryType } from './useGetBeneficiaries.query';
import { useHasAccessToEditBeneficiaries } from './useHasBeneficiaryAccess';

interface StyledButtonProps {
  isWarning?: boolean;
}

const StyledWrapper = styled(Box)`
  > div:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border2};
    padding-bottom: 12px;
    margin-bottom: 12px;
  }
`;

const StyledButton = styled(Button)<StyledButtonProps>`
  height: 21px !important;
  min-height: 21px !important;
  ${({ theme }) => theme.fonts.text14Regular};
  font-weight: 400 !important;
  margin-bottom: ${({ theme }) => theme.spacings.s};
  color: ${({ theme, isWarning }) => (isWarning ? `${theme.colors.danger} !important` : '')};
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    color: ${({ theme, isWarning }) => (isWarning ? theme.colors.danger : theme.colors.textAccent)};
  }
`;

interface Props {
  beneficiaries?: Beneficiary[];
  beneficiaryType?: BeneficiaryType;
  isRemovingAllPrimaryBeneficiariesButtonHidden?: boolean;
  isAddButtonDisabled?: boolean;
}

const BeneficiariesList: React.FC<Props> = ({
  beneficiaries,
  beneficiaryType,
  isAddButtonDisabled,
  isRemovingAllPrimaryBeneficiariesButtonHidden,
}) => {
  const hasAccessToEditBeneficiaries = useHasAccessToEditBeneficiaries();
  const typeTitle = useMemo(() => capitalize(beneficiaryType), [beneficiaryType]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isRemovingModalShown, setIsRemovingModalShown] = useState(false);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const onSuccess = useCallback(() => {
    setIsRemovingModalShown(false);
    setIsRemoving(false);
    handleAddPermanentSnackbar({
      text: `${typeTitle} beneficiaries has been removed`,
      state: SnackbarState.positive,
      closeIcon: true,
    });
  }, [setIsRemovingModalShown, setIsRemoving, handleAddPermanentSnackbar, typeTitle]);
  const { deleteBeneficiaries } = useDeleteBeneficiariesQuery({
    beneficiaryType,
    onSuccess,
  });

  const { isLimitedUseWhileTerminationInProgress } = useEmployerTerminationBanner();

  return (
    <Box>
      <Box pad={{ vertical: 'spacing24' }}>
        <Inscription size="20px" color="textTitle" lineHeight="28px">
          {typeTitle} Beneficiaries
        </Inscription>
      </Box>
      <Box
        data-testId={`BeneficiariesSection_${beneficiaryType}`}
        round="moduleRound"
        pad="spacing24"
        background="module"
      >
        <Box
          height="40px"
          align="center"
          justify="between"
          margin={{ bottom: 'spacing16' }}
          direction="row"
        >
          <Inscription
            color="textTitle"
            weight="bold"
            lineHeight="22px"
            size="large"
          >
            {typeTitle} Beneficiaries: {beneficiaries?.length}
          </Inscription>
          {beneficiaries?.length || !hasAccessToEditBeneficiaries ? null : (
            <ButtonHintForTerminatedEmployer
              button={
                <AppButton
                  testId={`BeneficiariesList_add_${beneficiaryType}`}
                  disabled={isAddButtonDisabled || isLimitedUseWhileTerminationInProgress}
                  buttonType="secondary"
                  onClick={() => setIsModalShown(true)}
                >
                  Add {typeTitle} Beneficiary
                </AppButton>
              }
              isTerminatedEmployer={isLimitedUseWhileTerminationInProgress}
              hintText='Terminated Organization cannot add Beneficiary.'
            />
          )}
          {isModalShown ? (
            <AddBeneficiaryModal
              beneficiaryType={beneficiaryType}
              onClose={() => setIsModalShown(false)}
              beneficiaries={beneficiaries}
            />
          ) : null}
          {isRemovingModalShown ? (
            <PendingModal
              visible
              onSetVisible={() => setIsRemovingModalShown(false)}
              header={`Remove ${typeTitle} Beneficiaries?`}
            >
              <Box direction="row">
                <Box margin={{ right: 'spacing12' }}>
                  <AppButton
                    testId='cancel_removing_beneficiary'
                    onClick={() => setIsRemovingModalShown(false)}
                    width="140px"
                    buttonType="secondary"
                  >
                    Cancel
                  </AppButton>
                </Box>
                <AppButton
                  testId='remove_beneficiary'
                  onClick={() => {
                    deleteBeneficiaries();
                    setIsRemoving(true);
                  }} 
                  width="140px"
                  color="red"
                  disabled={isRemoving}
                >
                  {isRemoving ? <Preloader /> : 'Remove'}
                </AppButton>
              </Box>
            </PendingModal>
          ) : null}
        </Box>

        <Box
          height="32px"
          background="legend"
          round="8px"
          align="center"
          pad={{ horizontal: 'spacing16' }}
          margin={{ bottom: 'spacing8' }}
          direction="row"
        >
          <Box width="350px">
            <Inscription color="textBody" weight="bold" lineHeight="18px" size="small">
              Name
            </Inscription>
          </Box>
          <Box width="300px">
            <Inscription color="textBody" weight="bold" lineHeight="18px" size="small">
              Allocation percentage
            </Inscription>
          </Box>
          <Box>
            <Inscription color="textBody" weight="bold" lineHeight="18px" size="small">
              Relationship
            </Inscription>
          </Box>
        </Box>

        {beneficiaries?.length ? (
          <Box
            round="8px"
            background="canvas"
            pad={{ vertical: 'spacing18', horizontal: 'spacing16' }}
            border={{ size: 'small', color: 'border2' }}
            data-testId={`BeneficiariesList_${beneficiaryType}`}
            direction="row"
            justify="between"
            align="center"
          >
            <StyledWrapper flex>
              {beneficiaries?.map((beneficiary) => <BeneficiariesListItem key={beneficiary?.id} beneficiary={beneficiary} />)}
            </StyledWrapper>
            {hasAccessToEditBeneficiaries ? (
              <Box data-testId="BeneficiariesList_edit_remove_buttons">
                <DropButton
                  contentStyle={{
                    left: '-200px',
                    ...isRemovingAllPrimaryBeneficiariesButtonHidden ? {
                      top: '-35px',
                    } : {}
                  }}
                >
                  <StyledButton
                    type="button"
                    data-testId="BeneficiariesList_edit_button"
                    onClick={() => setIsModalShown(true)}
                  >
                    Edit
                  </StyledButton>
                  {isRemovingAllPrimaryBeneficiariesButtonHidden ? null : (
                    <StyledButton
                      type="button"
                      isWarning
                      data-testId="BeneficiariesList_remove_button"
                      onClick={() => setIsRemovingModalShown(true)}
                    >
                      Remove
                    </StyledButton>
                  )}
                </DropButton>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box
            justify="center"
            align="center"
            pad={{ top: 'spacing24' }}
            data-testId={`no_${beneficiaryType}_beneficiaries`}
          >
            <Inscription weight="bold" lineHeight="20px" color="textTitle">
              There are no beneficiaries added yet
            </Inscription>
            <Box margin={{ top: 'spacing16' }}>
              <NoUsersIcon />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BeneficiariesList;
